import {
  evaluateExteriorPlacement,
  evaluateInteriorPlacement,
  interiorCapacity
} from "./placement-rules.js";
import { PLACEMENT_LIMIT, PLACEMENT_SCALE_MAX_STEP, type CatalogItem, type PropertyState } from "./types.js";

/** Categories that can stand in a yard. */
const PLACEABLE_OUTDOORS = new Set(["exterior", "rare", "plant"]);

/** How far a plant comes on, and how much closer a pet gets, for one qualifying participation. */
export const GROWTH_PER_PARTICIPATION = 1;
export const AFFECTION_PER_PARTICIPATION = 3;
export const MAX_GROWTH = 4;
export const MAX_AFFECTION = 100;

/**
 * A property after one more day of taking part.
 *
 * Plants come on a stage and pets warm to you a little. Both are capped and neither ever goes
 * backwards — nothing in this village punishes absence, so there is no decay to write.
 *
 * The rule lives here because both sides need it and they must agree: the Activity applies it
 * optimistically so the garden moves the moment the quest is confirmed, and the Worker applies it
 * for real. Only the Worker's answer is kept.
 */
export function applyParticipationGrowth(property: PropertyState): PropertyState {
  return {
    ...property,
    pets: property.pets.map((pet) => ({ ...pet, affection: Math.min(MAX_AFFECTION, pet.affection + AFFECTION_PER_PARTICIPATION) })),
    plants: property.plants.map((plant) => ({ ...plant, growth: Math.min(MAX_GROWTH, plant.growth + GROWTH_PER_PARTICIPATION) }))
  };
}

/**
 * Whether a save is trying to move growth or affection.
 *
 * These are earned by participating, so a property save is never the thing that changes them.
 * The client sends back the values it was given; anything else is a stale client or a crafted
 * request, and either way the answer is the same.
 */
export function growthWasTamperedWith(saved: PropertyState, stored: PropertyState): boolean {
  const storedPlants = new Map(stored.plants.map((plant) => [plant.id, plant.growth]));
  const storedPets = new Map(stored.pets.map((pet) => [pet.id, pet.affection]));
  // A plant or pet that is new in this save has no stored value to disagree with. It starts at
  // nothing, which is what `plant` and `adoptPet` already send.
  return (
    saved.plants.some((plant) => storedPlants.has(plant.id) && storedPlants.get(plant.id) !== plant.growth) ||
    saved.plants.some((plant) => !storedPlants.has(plant.id) && plant.growth !== 0) ||
    saved.pets.some((pet) => storedPets.has(pet.id) && storedPets.get(pet.id) !== pet.affection) ||
    saved.pets.some((pet) => !storedPets.has(pet.id) && pet.affection !== 0)
  );
}

/**
 * Categories that can stand in a room.
 *
 * Furniture, which was drawn for indoors, and plants, which come in pots — a houseplant on a
 * windowsill is half the reason to have a windowsill. Yard ornaments stay out: a bird bath in the
 * bedroom is the kind of joke that stops being funny the second time you see it in a screenshot.
 */
const PLACEABLE_INDOORS = new Set(["furniture", "plant"]);

/**
 * Everything wrong with a property's placements, in plain language.
 *
 * The client checks the same rules before it writes anything optimistically, so reaching an error
 * here normally means a stale client or a hand-made request. The messages are still written for a
 * member to read, because the client surfaces them verbatim.
 */
export function validatePropertyPlacements(
  property: PropertyState,
  catalog: CatalogItem[],
  ownedItemIds: string[]
): string[] {
  const errors: string[] = [];
  const items = new Map(catalog.map((item) => [item.id, item]));
  const seenIds = new Set<string>();
  const placedItemIds = new Set<string>();

  if (property.placements.length > PLACEMENT_LIMIT) {
    errors.push(`A home can show ${PLACEMENT_LIMIT} things at once`);
  }

  // Counted up front rather than as the loop goes, so the message says the home is full instead of
  // blaming whichever placement happened to be last in the array.
  const indoors = property.placements.filter((placement) => placement.surface === "interior");
  const capacity = property.homeType ? interiorCapacity(property.homeType) : 0;
  if (indoors.length > capacity) {
    errors.push(
      property.homeType
        ? `Your home holds ${capacity} things inside`
        : "There is nowhere indoors until you have a home"
    );
  }

  for (const placement of property.placements) {
    const item = items.get(placement.itemId);

    if (seenIds.has(placement.id)) errors.push(`Two placements share the id ${placement.id}`);
    seenIds.add(placement.id);

    if (!item) {
      errors.push(`Item ${placement.itemId} is not in the catalog`);
      continue;
    }
    if (!ownedItemIds.includes(item.id)) errors.push(`${item.name} is not owned`);
    if (!item.stackable) {
      if (placedItemIds.has(item.id)) errors.push(`${item.name} is already placed`);
      placedItemIds.add(item.id);
    }

    // Size and finish mean the same thing on either side of the wall, so they are checked before
    // the surfaces part ways.
    if (placement.scaleStep > PLACEMENT_SCALE_MAX_STEP) {
      errors.push(`${item.name} cannot be larger than the size it was made`);
    }

    for (const [variant, value] of Object.entries(placement.selectedVariants)) {
      if (!item.variants[variant]?.includes(value)) {
        errors.push(`${item.name} does not include the selected ${variant}`);
      }
    }

    if (placement.surface === "interior") {
      // Indoors is the inside of the home that stands on the parcel, so there is no indoors until
      // one does. The space, and how much of it there is, comes from which home was chosen.
      if (!property.homeType) {
        errors.push(`${item.name} is indoors, but there is no home yet`);
        continue;
      }
      if (!PLACEABLE_INDOORS.has(item.category)) {
        errors.push(`${item.name} cannot stand indoors`);
        continue;
      }

      const check = evaluateInteriorPlacement({
        itemId: item.id,
        itemName: item.name,
        position: placement.position,
        rotationQuarters: placement.rotationQuarters,
        scaleStep: placement.scaleStep,
        homeType: property.homeType,
        // Everything else indoors, so a thing may rest on a table but never inside one.
        others: indoors,
        ignoreId: placement.id
      });
      if (!check.ok) errors.push(check.message);
      continue;
    }

    if (!PLACEABLE_OUTDOORS.has(item.category)) {
      errors.push(`${item.name} cannot stand outdoors`);
      continue;
    }

    const check = evaluateExteriorPlacement({
      itemId: item.id,
      itemName: item.name,
      position: placement.position,
      rotationQuarters: placement.rotationQuarters,
      scaleStep: placement.scaleStep,
      plotTier: property.plotTier,
      homeType: property.homeType
    });
    if (!check.ok) errors.push(check.message);
  }

  return errors;
}
